'use client';

import {
  createContext,
  useEffect,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';

import { logger } from '@/lib/logger';

import type { UserData } from '@/lib/api/types';

type AuthContextValue = {
  isAuthenticated: boolean;
  loading: boolean;
  userDataLoading: boolean;
  getUserDataField: <T>(
    selector: (user: UserData) => T | undefined,
  ) => T | undefined;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const useAuthenticationData = () => {
  const session = useSessionContext();

  const isAuthenticated = !session.loading && session.doesSessionExist;
  const userId = isAuthenticated ? session.userId : undefined;

  return { isAuthenticated, userId, loading: session.loading };
};

// 🧩 Internal: reads localStorage + listens for updates
const useUserData = (): UserData | null => {
  const [userData, setUserData] = useState<UserData | null>(null);

  useEffect(() => {
    const handler = () => {
      try {
        const raw = localStorage.getItem('userData');
        if (raw) {
          const parsed = JSON.parse(raw);
          setUserData(parsed);
        } else {
          setUserData(null);
        }
      } catch (e) {
        logger.error(e);
        setUserData(null);
      }
    };

    handler();
    window.addEventListener('userDataUpdated', handler);
    return () => window.removeEventListener('userDataUpdated', handler);
  }, []);

  return userData;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, loading } = useAuthenticationData();
  const userData = useUserData();
  const userDataLoading = isAuthenticated && userData === null;

  const getUserDataField = useMemo(() => {
    return <T,>(selector: (user: UserData) => T | undefined): T | undefined =>
      userData ? selector(userData) : undefined;
  }, [userData]);

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      loading,
      userDataLoading,
      getUserDataField,
    }),
    [isAuthenticated, loading, userDataLoading, getUserDataField],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('AuthContext must be used within <AuthProvider>');
  }

  return ctx;
};
